import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack,
  Folder,
  CheckCircle,
  Schedule,
  AttachMoney,
  Chat,
  Info,
  CloudUpload,
} from '@mui/icons-material';
import { GradientCard, StatCard } from './gradientCard';

const CollaboratorView = ({ 
  castingCall, 
  onBack, 
  onOpenChat, 
  currentUser 
}) => {
  const theme = useTheme();
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);

  // Get collaborator-specific data
  const applicant = castingCall.applicants?.find(app => {
    if (castingCall.call_type === 'creator') {
      return app.username === currentUser?.username;
    } else {
      return app.id === currentUser?.id;
    }
  });

  const assignedPayout = applicant?.assigned_payout || 0;
  
  // Calculate checklist completion
  const allChecklistItems = castingCall.organizers?.flatMap(org => 
    org.checklist_items || []
  ) || [];
  
  const completedItems = allChecklistItems.filter(item => item.completed).length;
  const totalItems = allChecklistItems.length;
  const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  // Get accessible organizers
  const accessibleOrganizers = castingCall.organizers?.filter(org => 
    org.can_access
  ) || [];

  const displayName = castingCall.call_type === 'creator'
    ? applicant?.display_name || applicant?.username
    : `${applicant?.first_name || ''} ${applicant?.last_name || ''}`.trim() || applicant?.email;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{ mb: 2 }}
        >
          Back to Collaborations
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {castingCall.brief}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {castingCall.description || 'Complete your deliverables for this casting call'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={castingCall.call_type === 'creator' ? 'Creator Call' : 'General Work'}
                color="primary"
                size="small"
              />
              <Chip
                label={castingCall.status}
                color={castingCall.status === 'ACTIVE' ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label="Collaborator"
                color="secondary"
                size="small"
                icon={<CheckCircle />}
              />
            </Stack>
          </Box>

          <Button
            variant="contained"
            startIcon={<Chat />}
            onClick={() => onOpenChat(applicant)}
          >
            Contact Brand
          </Button>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircle />}
            label="Completed Items"
            value={`${completedItems}/${totalItems}`}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Schedule />}
            label="Progress"
            value={`${Math.round(completionPercentage)}%`}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AttachMoney />}
            label="Your Payout"
            value={assignedPayout > 0 ? `$${assignedPayout.toFixed(2)}` : 'Not Set'}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Folder />}
            label="File Organizers"
            value={accessibleOrganizers.length}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Progress Overview */}
      <GradientCard sx={{ mb: 4 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Deliverables Progress
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {completedItems} of {totalItems} items completed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(completionPercentage)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                },
              }}
            />
          </Box>

          {completionPercentage === 100 ? (
            <Alert severity="success" icon={<CheckCircle />}>
              🎉 All deliverables completed! Great work!
            </Alert>
          ) : (
            <Alert severity="info" icon={<Info />}>
              {totalItems - completedItems} item{totalItems - completedItems !== 1 ? 's' : ''} remaining. Keep going!
            </Alert>
          )}
        </Box>
      </GradientCard>

      {/* Organizers & Checklists */}
      <GradientCard>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Your File Organizers ({accessibleOrganizers.length})
          </Typography>

          {accessibleOrganizers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Folder sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No organizers shared yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                The brand will share file organizers with you once you're selected
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {accessibleOrganizers.map((organizer) => (
                <Grid item xs={12} md={6} key={organizer.id}>
                  <Card
                    sx={{
                      height: '100%',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                            }}
                          >
                            <Folder />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {organizer.display_name || organizer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {organizer.file_count || 0} files
                            </Typography>
                          </Box>
                        </Box>
                        
                        {organizer.is_primary && (
                          <Chip
                            label="Primary"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {organizer.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {organizer.description}
                        </Typography>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Checklist Stats */}
                      {organizer.checklist_stats && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              Checklist Progress
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {organizer.checklist_stats.completed_count}/{organizer.checklist_stats.total_count}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={organizer.checklist_stats.completion_percentage || 0}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: 'success.main',
                              },
                            }}
                          />
                        </Box>
                      )}

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          startIcon={<Folder />}
                          href={`/organizers/${organizer.organizer_id}`}
                        >
                          View Files
                        </Button>
                        {organizer.can_upload && (
                          <Tooltip title="You can upload files to this organizer">
                            <IconButton
                              size="small"
                              color="primary"
                              href={`/organizers/${organizer.organizer_id}`}
                            >
                              <CloudUpload />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </GradientCard>
    </Container>
  );
};

export default CollaboratorView;